import { Link, Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { DataGridPro, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid-pro';
import PropTypes from 'prop-types';
import React, { FC, memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { AppState, store, useAppSelector } from 'src/store';
import { setDocumentDetail } from 'src/store/documentDetail';
import { setSnackbarMessage } from 'src/store/snackbarMessage';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import DocManagementService from '../documentUpload/service/DocManagementService';
import { CustomPagination } from 'src/util/DataTableFooter';
import { Loader } from 'src/components/common/Loader';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));

const VersionHistory: FC<any> = ({
  openVersionHistory,
  setOpenVersionHistory,
  setAnchorEl,
  setFileId,
  setDataGridComponent,
  setDocumentToDisplay1,
  setDisableButton,
  setDisableToISF
}: any) => {
  const dispatch = useDispatch();
  const docService = new DocManagementService();
  const documentDetail: any = useSelector((state: AppState) => state.documentDetail);
  const [versionHistoryDetails, setVersionHistoryDetails] = useState<any>([]);
  const [pageSize, setPageSize] = React.useState<number>(10);
  const { preferedDateFormat } = usePreferedDateFormat();
  const [loading, setLoading] = useState<any>(false);
  const user: any = useAppSelector(state => state.user.user);
  const selectedStudy = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Study);
  const selectedCountry = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Country);
  const selectedSite = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Site);
  const [loaderStatus, setLoaderStatus] = useState(false);

  let TmfLevel =
    (selectedStudy === '--' || selectedStudy === '') &&
    (selectedCountry === '--' || selectedCountry == '') &&
    (selectedSite === '--' || selectedSite === '')
      ? '--'
      : (selectedCountry === '--' || selectedCountry == '') && (selectedSite === '--' || selectedSite === '')
      ? 'Study'
      : selectedSite === '--' && selectedCountry != '--'
      ? 'Country'
      : 'Site';
  if (TmfLevel === '--') {
    TmfLevel = 'Study';
  }
  const params = {
    module_section_related: 'DocumentDetails',
    parent_entity_related: documentDetail.data[0].tmf_document_name
  };

  const getVersionHistory = async () => {
    setLoading(true);
    const result = await docService.getVersionHistory(
      documentDetail.data[0].tmf_document_id,
      selectedStudy,
      selectedCountry,
      selectedSite,
      TmfLevel,
      params
    );
    const updated = result.data.map((res: any) => {
      return { id: res.document_version.document_id, fileId: res.file_master_id, ...res.document_version };
    });
    setVersionHistoryDetails(updated);
    setLoading(false);
  };

  useEffect(() => {
    getVersionHistory();
  }, []);

  const getVesionBasedDocumentDetails = async (vd: any) => {
    const params = {
      module_section_related: 'DocumentDetails',
      parent_entity_related: documentDetail.data[0].tmf_document_name
    };
    const site = vd.tmf_doc_level === 'Site' ? vd.tmf_doc_site : '--';
    const country = vd.tmf_doc_level === 'Country' || vd.tmf_doc_level === 'Site' ? vd.tmf_doc_country : '--';
    const res: any = await docService.getVesionBasedDocumentDetails(
      vd.tmf_doc_study,
      site,
      country,
      vd.tmf_document_id,
      vd.tmf_doc_version_number,
      TmfLevel,
      params
    );
    if (res.data) {
      const latestVersionDetails = versionHistoryDetails[0];

      const result: any = await docService.getQualityControlDetailsById(
        res.data.document_data.id,
        selectedStudy,
        selectedCountry,
        selectedSite,
        TmfLevel,
        params
      );
      const qualityControl = result?.data?.quality_control;
      const docDetailsObj: any = [
        {
          ...documentDetail.data[0],
          id: res?.data?.document_data?.id,
          tmf_doc_latest_version: latestVersionDetails?.tmf_doc_version_number,
          fileId: res.data.file_master.id,
          tmf_doc_version_number: res.data.document_data.tmf_doc_version_number,
          tmf_document_name: res.data.document_data.tmf_document_name,
          tmf_doc_upload_date: res.data.document_data.tmf_doc_upload_date,
          tmf_document_id: res.data.document_data.tmf_document_id,
          tmf_doc_level: res.data.document_data.tmf_doc_level,
          tmf_doc_study: res.data.document_data.tmf_doc_study,
          tmf_doc_viewer_department: res.data.document_data.tmf_doc_viewer_department,
          tmf_artifact_number: res.data.document_data.tmf_artifact_number,
          tmf_artifact_name: res.data.document_data.tmf_artifact_name,
          tmf_doc_type: res.data.document_data.tmf_doc_type,
          tmf_doc_subtype: res.data.document_data.tmf_doc_subtype,
          tmf_doc_milestone: res.data.document_data.tmf_doc_milestone,
          tmf_doc_created_by: res.data.document_data.tmf_doc_created_by,
          tmf_doc_status: res.data.document_data.tmf_doc_status,
          tmf_raw_file_name: res.data.file_master.tmf_raw_file_name,
          tmf_doc_size: res.data.file_master.tmf_doc_size,
          docFormat: res.data.file_master.tmf_doc_type,
          tmf_doc_viewer_pages: res.data.file_master.tmf_doc_viewer_pages,
          tmf_doc_viewer_qc_issue_type: qualityControl?.tmf_doc_viewer_qc_issue_type,
          tmf_doc_viewer_qc_issue_comments: qualityControl?.tmf_doc_viewer_qc_issue_comments,
          is_checkbox_verified: qualityControl?.is_checkbox_verified,
          tmf_doc_viewer_certified_copy:
            qualityControl !== undefined
              ? qualityControl?.tmf_doc_viewer_req_certified_copy
              : res.data.document_data.tmf_doc_viewer_req_certified_copy,
          is_submit: qualityControl?.is_submit,
          is_active: qualityControl?.is_active,
          tmf_doc_last_modified_by: res.data.document_data?.tmf_doc_last_modified_by
        }
      ];
      setDocumentDetail(dispatch, docDetailsObj);
      return docDetailsObj;
    } else {
      store.dispatch(setSnackbarMessage('Error in getting Version History data', 'error'));
    }
  };

  const gridFunc = async (versionDetails: any) => {
    setLoaderStatus(true);
    setFileId(versionDetails.fileId);
    const resultOfDoc = await getVesionBasedDocumentDetails(versionDetails);
    if (resultOfDoc[0].tmf_doc_latest_version === resultOfDoc[0].tmf_doc_version_number) {
      setDisableButton(false);
      setDisableToISF(false);
    } else {
      setDisableButton(true);
      setDisableToISF(true);
    }
    setDocumentToDisplay1({ data: resultOfDoc });
    handleClose();
    setDataGridComponent && setDataGridComponent(false);
    setLoaderStatus(false);
  };

  const columns: GridColDef[] = [
    {
      field: 'tmf_doc_version_number',
      headerName: 'Version',
      width: 280,
      renderCell: (params: any) => (
        <Link onClick={() => gridFunc(params.row)}>{params.value!.toFixed(1).toString()}</Link>
      )
    },
    {
      field: 'tmf_doc_last_modified_by',
      headerName: 'Name',
      width: 280
    },
    {
      field: 'tmf_doc_upload_date',
      headerName: 'Date Modified',
      width: 280,
      valueFormatter: (params: GridValueFormatterParams) => {
        return preferedDateFormat(params.value);
      }
    }
  ];

  const handleClose = () => {
    setOpenVersionHistory(false);
    setAnchorEl(null);
  };

  function TabPanel(props: { [x: string]: any; children: any; value: any; index: any }) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  }

  TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
  };
  return (
    <div>
      <div className="query-modal">
        <PopupComponent aria-labelledby="customized-dialog-title" open={openVersionHistory} maxWidth="md">
          <Loader open={loaderStatus} />
          <DialogContent dividers className="audit-history-modal">
            <div className="audit-card">
              <Grid container spacing={3}>
                <Grid md={12}>
                  <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex', pt: 0 }}>
                    <Grid container>
                      <Grid container>
                        <Grid md={12}>
                          <h2>{`Version History: ${documentDetail.data[0].tmf_document_name}`}</h2>
                        </Grid>
                      </Grid>
                      <Grid md={12}>
                        <div style={{ height: 360, width: '100%' }}>
                          <DataGridPro
                            rows={versionHistoryDetails}
                            columns={columns}
                            pageSize={pageSize}
                            components={{ Pagination: CustomPagination }}
                            onPageSizeChange={newPageSize => setPageSize(newPageSize)}
                            rowsPerPageOptions={[5, 10, 20]}
                            pagination
                            disableSelectionOnClick
                            initialState={{
                              sorting: {
                                sortModel: [{ field: 'updated_on', sort: 'desc' }]
                              }
                            }}
                            loading={loading}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item md={12}>
                  <Grid container spacing={2}>
                    <Grid item md={12} display="flex" justifyContent="flex-end">
                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="text"
                          href="#contained-buttons"
                          className="grey-btn"
                          onClick={handleClose}
                          disabled={loading}
                        >
                          Close
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </div>
          </DialogContent>
        </PopupComponent>
      </div>
    </div>
  );
};

export default memo(VersionHistory);
